import React, { useState, useEffect } from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Container from "react-bootstrap/Container";

import { courseDb } from "../../../db/dbClasses";
import { PDFIcon } from "../../Miscellaneous/Icons.component";
import { BoxDiv } from "../../Miscellaneous/FancyDiv.component";
import Header from "../../Miscellaneous/Header.component";
import SubHeader from "../../Miscellaneous/SubHeader.component";

const CourseObject = ({
  title,
  semester,
  year,
  link,
  description,
  outline,
  dept,
  section,
  number,
  id,
  timeType,
  ...rest
}) => {
  let boxColors;

  switch (timeType) {
    case "current":
      boxColors = {
        top: "rgb(0, 214, 137)",
        bottom: "rgb(0, 196, 125)",
      };
      break;
    case "previous":
      boxColors = {
        top: "rgb(187, 187, 187)",
        bottom: "rgb(151, 151, 151)",
      };
      break;
    case "future":
      boxColors = {
        top: "rgb(2, 117, 216)",
        bottom: "rgb(2, 69, 216)",
      };
      break;
    default:
      boxColors = {
        top: "rgb(0, 214, 137)",
        bottom: "rgb(0, 196, 125)",
      };
      break;
  }

  return (
    <Container fluid className="font-open-sans">
      <Row>
        <Col xs={6} md={4} lg={3} className="text-center">
          <Row>
            <Col
              className="text-white p-1 fw-b"
              style={{
                backgroundColor: boxColors.top,
              }}
            >
              {semester}
            </Col>
          </Row>
          <Row>
            <Col
              className="text-white p-1 fw-b"
              style={{
                backgroundColor: boxColors.bottom,
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.4) inset",
              }}
            >
              {year}
            </Col>
          </Row>
        </Col>
        <Col
          // className="w-100"
          xs={6}
          md={8}
          lg={9}
        >
          <h5>
            {link && (
              <a href={link || "/teaching"} target="_blank" rel="noreferrer">
                {dept}-{number}-{section}
                {"  "}
                {title}
              </a>
            )}
            {!link && (
              <span>
                {dept}-{number}-{section}
                {"  "}
                {title}
              </span>
            )}
            {outline && (
              <a href={outline || "/teaching"} target="_blank" rel="noreferrer">
                <span>
                  {"  "}
                  <PDFIcon />
                </span>
              </a>
            )}
          </h5>
          <p>{description}</p>
        </Col>
      </Row>
    </Container>
  );
};

const semesterComparator = (a, b) => {
  try {
    let a_year = Number.parseInt(a.year);
    let b_year = Number.parseInt(b.year);

    if (a_year > b_year) return 1;
    if (a_year < b_year) return -1;

    let a_term = a?.semester?.toString().toLowerCase();
    let b_term = b?.semester?.toString().toLowerCase();

    let terms = ["winter", "spring", "summer", "fall", "fall/winter"];
    let a_index = terms.indexOf(a_term);
    let b_index = terms.indexOf(b_term);

    if (a_index === -1 || b_index === -1) return 0;
    if (a_index === b_index) return 0;
    if (a_index > b_index) return 1;
    return -1;
  } catch (err) {
    return 0;
  }
};

const _separateCourses = (courses) => {
  const today = new Date();

  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();

  let currentSemester;

  if (0 <= todayMonth && todayMonth <= 4) {
    currentSemester = "Winter";
  } else if (5 <= todayMonth && todayMonth <= 6) {
    currentSemester = "Spring";
  } else if (7 <= todayMonth && todayMonth <= 8) {
    currentSemester = "Summer";
  } else {
    currentSemester = "Fall";
  }

  let currentTerm = {
    semester: currentSemester,
    year: todayYear,
  };

  let previous = [];
  let current = [];
  let future = [];

  courses.forEach((elem) => {
    try {
      let val = semesterComparator(currentTerm, elem);
      if (val === -1) {
        future.push(elem);
      } else if (val === 1) {
        previous.push(elem);
      } else {
        current.push(elem);
      }
    } catch (err) {
      current.push(elem);
    }
  });

  return {
    previous,
    current,
    future,
  };
};

const Teaching = () => {
  // const [courses, setCourses] = useState([])

  const [previousCourses, setPreviousCourses] = useState([]);
  const [currentCourses, setCurrentCourses] = useState([]);
  const [futureCourses, setFutureCourses] = useState([]);

  useEffect(() => {
    (async () => {
      let array = await courseDb.read();

      let { previous, current, future } = _separateCourses(array);
      setPreviousCourses(previous);
      setCurrentCourses(current);
      setFutureCourses(future);
    })();
  }, []);

  return (
    <div className="p-0" style={{ minHeight: "100vh" }}>
      <Header content="Teaching" />

      <BoxDiv topColor={"#F3F3F3"} bgColor={"#FFFFFF"}>
        {currentCourses.length > 0 && (
          <div className="w-100 p-5 pt-2">
            <SubHeader id="current" content="Current" />
            <div className="p-1 pt-4">
              {[...currentCourses]
                .sort(semesterComparator)
                .reverse()
                .map((elem) => (
                  <CourseObject
                    key={`${elem.dept}-${elem.number}-${elem.section}-${elem.semester}-${elem.year}`}
                    {...elem}
                    timeType="current"
                  />
                ))}
            </div>
          </div>
        )}
        {previousCourses.length > 0 && (
          <div className="w-100 p-5 pt-2">
            <SubHeader id="history" content="History" />
            <div className="p-1 pt-4">
              {[...previousCourses]
                .sort(semesterComparator)
                .reverse()
                .map((elem) => (
                  <CourseObject
                    key={`${elem.dept}-${elem.number}-${elem.section}-${elem.semester}-${elem.year}`}
                    {...elem}
                    timeType="previous"
                  />
                ))}
            </div>
          </div>
        )}
        {futureCourses.length > 0 && (
          <div className="w-100 p-5 pt-2">
            <SubHeader id="future" content="Future" />
            <div className="p-1 pt-4">
              {[...futureCourses]
                .sort(semesterComparator)
                .reverse()
                .map((elem) => (
                  <CourseObject
                    key={`${elem.dept}-${elem.number}-${elem.section}-${elem.semester}-${elem.year}`}
                    {...elem}
                    timeType="future"
                  />
                ))}
            </div>
          </div>
        )}
      </BoxDiv>
    </div>
  );
};

export default Teaching;
